import React, {Fragment, useEffect} from "react";
import {useRouter} from "next/router";
import shopApi from "../../api/shop";
import BlogPageCategory from "../../components/blog/BlogPageCategory";
import {IPageProps} from "../../types/pages";
import {GetServerSideProps, NextPage} from "next";
import {domainUrl} from "../../helper";
import Head from "next/head";
import MainLayout from "../../components/ForCWV/Wrappers/MainLayout";
import {layoutTranslations} from "../../services/translationKeys";
import {layoutProps} from "../../services/layoutProps";

const PageBlog: NextPage<IPageProps> = (pageProps) => {
    const {
        dbName,
        domain,
        selectedLocale,
        translationsData,
        breadcrumb,
        blog,
        siteName,
        defaultLangCode,
        locales
    } = pageProps
    const router = useRouter();
    const {query} = router;
    const meta_title = translationsData.Blog
    const meta_description = "Blog page"
    const meta_keywords = ""
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)

    useEffect(() => {
        window.history.replaceState(null, "", window.location.pathname);
    }, [query.pageSlug, selectedLocale]);

    return (
        <MainLayout {...pageProps}>
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="robots" content="index, follow"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <meta property="og:site_name" content={siteName}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <BlogPageCategory
                    pageSlug={query.pageSlug}
                    breadcrumb={breadcrumb}
                    dbName={dbName}
                    domain={domain}
                    blog={blog}
                    translationsData={translationsData}
                    forBlogType={"blog"}
                    selectedLocale={selectedLocale}
                />
            </Fragment>
        </MainLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async ({locale, req, query}) => {
    const {page = 1} = query;
    let blog;
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: layoutTranslations,
        ipv4: typedReq.ipv4
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Blog: BlogIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const options = {
        locale: selectedLocale,
        page: page,
        limit: 18,
        dbName: typedReq.defaultDB.db_name
    }

    const blogs = await shopApi.getAsyncBlogs(options, typedReq.m)
    blog = {
        data: blogs.data,
        total: blogs?.meta?.total || 3,
        current_page: page || blogs.meta.current_page,
    };

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: BlogIntl, url: ``}
    ]

    return {
        props: {
            blog,
            breadcrumb,
            locale: selectedLocale,
            ...wrapperProps,
        },
    };
}

export default PageBlog
